import React, { CSSProperties, JSX, useCallback, useRef, useState } from 'react';
import '../../styles/CometChatAddMembers/CometChatAddMembers.css';
import { CometChat } from '@cometchat/chat-sdk-javascript';
import { useCometChatAddMembers } from './useCometChatAddMembers';
import backbutton from '../../assets/arrow_back.svg';
import {
  CometChatButton,
  CometChatGroupEvents,
  CometChatOption,
  CometChatUIKitConstants,
  CometChatUIKitUtility,
  CometChatUsers,
  SelectionMode,
  getLocalizedString,
  useCometChatErrorHandler,
  useRefSync,
} from '@cometchat/chat-uikit-react';
import { useCometChatContext } from '../../context/CometChatContext';
import axios from 'axios';
import { COMETCHAT_CONSTANTS } from 'index';
import { isEmailEnabled } from 'CometChat/utils/emailNotificationCheck';

interface IAddMembersProps {
  /**
   * Image URL for the back button
   *
   * @remarks
   * This prop will also be used if `backButton` prop is not provided
   *
   * @defaultValue `./assets/backbutton.svg`
   */
  backButtonIconURL?: string;
  /**
   * Show back button
   *
   * @defaultValue `true`
   */
  showBackButton?: boolean;
  /**
   * Function to call when the back button is clicked
   */
  onBack?: () => void;
  /**
   * Title of the component
   *
   * @defaultValue `getLocalizedString("ADD_MEMBERS")`
   */
  title?: string;
  /**
   * Hide the search bar
   *
   * @defaulValue `false`
   */
  hideSearch?: boolean;
  /**
   * Image URL for the search icon to use in the search bar
   *
   * @defaultValue `./assets/search.svg`
   */
  searchIconURL?: string;
  /**
   * Text to be displayed when the search input has no value
   *
   * @defaultValue `getLocalizedString("SEARCH")`
   */
  searchPlaceholderText?: string;
  /**
   * Show alphabetical header
   *
   * @defaultValue `false`
   */
  showSectionHeader?: boolean;
  /**
   * Property on the user object
   *
   * @remarks
   * This property will be used to extract the section header character from the user object
   *
   * @defaultValue `getName`
   */
  sectionHeaderField?: keyof CometChat.User;
  /**
   * Image URL for the default loading view
   *
   * @defaultValue `./assets/spinner.svg`
   */
  loadingIconURL?: string;
  /**
   * Custom view for the loading state of the component
   */
  loadingStateView?: JSX.Element;
  /**
   * Text to display in the default empty view
   *
   * @defaultValue `getLocalizedString("NO_USERS_FOUND")`
   */
  emptyStateText?: string;
  /**
   * Custom view for the empty state of the component
   */
  emptyStateView?: JSX.Element;
  /**
   * Text to display in the default error view
   *
   * @defaultValue `getLocalizedString("SOMETHING_WRONG")`
   */
  errorStateText?: string;
  /**
   * Custom view for the error state of the component
   */
  errorStateView?: JSX.Element;
  /**
   * Hide error view
   *
   * @remarks
   * If set to true, hides the default and the custom error view
   *
   * @defaultValue `false`
   */
  hideError?: boolean;
  /**
   * Hide user presence
   *
   * @remarks
   * If set to true, the status indicator of the default list item view is not displayed
   *
   * @defaultValue `false`
   */
  disableUsersPresence?: boolean;
  /**
   * Hide the separator at the bottom of the default list item view
   *
   * @defaultValue `false`
   */
  hideSeparator?: boolean;
  /**
   * Function to call whenever the component encounters an error
   */
  onError?: ((error: CometChat.CometChatException) => void) | null;
  /**
   * Custom view to render on the top-right of the component
   */
  menus?: JSX.Element;
  /**
   * List of actions available on mouse over on the default list item component
   */
  options?: (user: CometChat.User) => CometChatOption[];
  /**
   * Selection mode to use for the default tail view
   *
   * @remarks
   * This prop is used if `listItemView` prop is not provided.
   *
   * @defaultValue `SelectionMode.multiple`
   */
  selectionMode?: SelectionMode;
  /**
   * Function to call when a user from the fetched list is selected
   *
   * @remarks
   * This prop is used if `selectionMode` prop is not `SelectionMode.none`
   */
  onSelect?: (user: CometChat.User, selected: boolean) => void;
  /**
   * Request builder to fetch users
   *
   * @remarks
   * If the search input is not empty and the `searchRequestBuilder` prop is not provided,
   * the search keyword of this request builder is set to the text in the search input
   *
   * @defaultValue Default request builder having the limit set to 30
   */
  usersRequestBuilder?: CometChat.UsersRequestBuilder;
  /**
   * Request builder with search parameters to fetch users
   *
   * @remarks
   * If the search input is not empty,
   * the search keyword of this request builder is set to the text in the search input
   */
  searchRequestBuilder?: CometChat.UsersRequestBuilder;
  /**
   * Custom list item view to be rendered for each user in the fetched list
   */
  listItemView?: (user: CometChat.User) => JSX.Element;
  /**
   * Custom subtitle view to be rendered for each user in the fetched list
   *
   * @remarks
   * This prop is used if `listItemView` prop is not provided
   */
  subtitleView?: (user: CometChat.User) => JSX.Element;
  /**
   * Group to add members to
   */
  group: CometChat.Group;
  /**
   * Function to call when add button of the component is clicked
   *
   * @remarks
   * This function won't be call if no users are selected
   */
  onAddMembersButtonClick?: (guid: string, membersToAdd: CometChat.GroupMember[]) => void;
  /**
   * Text to display for the default add button
   *
   * @defaultValue `getLocalizedString("ADD_MEMBERS")`
   */
  buttonText?: string;
  /**
   * Image URL for the close button
   *
   * @defaultValue `./assets/close2x.svg`
   */
  closeButtonIconURL?: string;
  /**
   * Function to call when the close button is clicked
   */
  onClose?: () => void;
  /**
   * Styles to apply to the status indicator component of the default list item view
   */
  statusIndicatorStyle?: CSSProperties;
}

/**
 * Renders a scrollable list of users to add to a group of a CometChat App
 */
export function CometChatAddMembers(props: IAddMembersProps) {
  const { chatFeatures } = useCometChatContext();
  const {
    showBackButton = false,
    onBack,
    hideSearch = false,
    showSectionHeader = false,
    sectionHeaderField = 'getName',
    loadingStateView,
    emptyStateView,
    errorStateView,
    hideError = false,
    onError,
    options,
    selectionMode = SelectionMode.multiple,
    onSelect,
    usersRequestBuilder: usersRequestBuilderProp,
    searchRequestBuilder,
    listItemView,
    subtitleView,
    group,
    onAddMembersButtonClick = null,
    buttonText = getLocalizedString('add_members'),
  } = props;

  // Build usersRequestBuilder with friendsOnly if needed
  let usersRequestBuilder = usersRequestBuilderProp;
  if (!usersRequestBuilder) {
    usersRequestBuilder = new CometChat.UsersRequestBuilder().setLimit(30);
    if (chatFeatures?.userManagement?.friendsOnly) {
      usersRequestBuilder = usersRequestBuilder.friendsOnly(true);
    }
  }

  const membersToAddRef = useRef<CometChat.GroupMember[]>([]);
  const selectionModeRef = useRef(selectionMode);
  const loggedInUserRef = useRef<CometChat.User | null>(null);
  const onSelectPropRef = useRefSync(onSelect);
  const groupPropRef = useRefSync(group);
  const onBackPropRef = useRefSync(onBack);
  const onAddMembersButtonClickPropRef = useRefSync(onAddMembersButtonClick);
  const errorHandler = useCometChatErrorHandler(onError!);
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [isError, setIsError] = useState(false);

  /**
    /**
     * Creates a `CometChat.GroupMember` instance from the provided `user`
     */
  const createGroupMemberFromUser = useCallback(
    (user: CometChat.User): CometChat.GroupMember => {
      const groupMember = new CometChat.GroupMember(
        user.getUid(),
        CometChatUIKitConstants.groupMemberScope.participant
      );
      groupMember.setName(user.getName());
      groupMember.setGuid(groupPropRef.current.getGuid());
      groupMember.setAvatar(user?.getAvatar());
      groupMember.setStatus(user.getStatus());
      return groupMember;
    },
    [groupPropRef]
  );

  const updateAddMembersList = useCallback(
    (user: CometChat.User) => {
      const targetUid = user.getUid();
      const tmpMembersToAddList: CometChat.GroupMember[] = [];
      let updated = false;
      for (let i = 0; i < membersToAddRef.current.length; i++) {
        const curMember = membersToAddRef.current[i];
        if (targetUid === curMember.getUid()) {
          updated = true;
        } else {
          tmpMembersToAddList.push(curMember);
        }
      }
      if (!updated) {
        tmpMembersToAddList.push(createGroupMemberFromUser(user));
      }
      membersToAddRef.current = tmpMembersToAddList;
    },
    [createGroupMemberFromUser]
  );

  /**
   * Updates `membersToAddRef`
   *
   * @remarks
   * This function makes sure `membersToAddRef` is in sync with the UI
   */
  const onSelectWrapper = useCallback(
    (user: CometChat.User, selected: boolean): void => {
      if (onSelectPropRef.current) {
        return onSelectPropRef.current(user, selected);
      }
      if (selectionModeRef.current === SelectionMode.single) {
        membersToAddRef.current = [createGroupMemberFromUser(user)];
      } else if (selectionModeRef.current === SelectionMode.multiple) {
        updateAddMembersList(user);
      }
      if (membersToAddRef.current.length === 0) {
        setIsDisabled(true);
      } else {
        setIsDisabled(false);
      }
    },
    [createGroupMemberFromUser, onSelectPropRef, updateAddMembersList]
  );

  /**
   * Creates a `CometChat.Action` instance
   */
  const createActionMessage = useCallback(
    (actionOn: CometChat.GroupMember, loggedInUser: CometChat.User, group: CometChat.Group): CometChat.Action => {
      const actionMessage = new CometChat.Action(
        group.getGuid(),
        CometChatUIKitConstants.MessageTypes.groupMember,
        CometChatUIKitConstants.MessageReceiverType.group,
        CometChatUIKitConstants.MessageCategory.action as CometChat.MessageCategory
      );
      actionMessage.setAction(CometChatUIKitConstants.groupMemberAction.ADDED);
      actionMessage.setActionBy(CometChatUIKitUtility.clone(loggedInUser));
      actionMessage.setActionFor(CometChatUIKitUtility.clone(group));
      actionMessage.setActionOn(CometChatUIKitUtility.clone(actionOn));
      actionMessage.setReceiver(CometChatUIKitUtility.clone(group));
      actionMessage.setSender(CometChatUIKitUtility.clone(loggedInUser));
      actionMessage.setConversationId('group_' + group.getGuid());
      actionMessage.setMuid(CometChatUIKitUtility.ID());
      actionMessage.setMessage(`${loggedInUser.getName()} added ${actionOn.getUid()}`);
      actionMessage.setSentAt(CometChatUIKitUtility.getUnixTimestamp());
      return actionMessage;
    },
    []
  );

  /**
   * Provides a default behaviour to the `onAddMembersButtonClick` prop
   */
  const onAddBtnClickWrapper = useCallback(async (): Promise<void> => {
    if (membersToAddRef.current.length === 0) {
      return;
    }
    setIsLoading(true);
    setIsError(false);
    try {
      const group = groupPropRef.current;
      const onAddBtnClick = onAddMembersButtonClickPropRef.current;
      if (onAddBtnClick) {
        onAddBtnClick(group.getGuid(), membersToAddRef.current);
        membersToAddRef.current = [];
        return;
      }
      const UIDsToRemove: Set<string> = new Set();
      const response = await CometChat.addMembersToGroup(group.getGuid(), membersToAddRef.current, []);
      setIsLoading(false);
      if (response) {
        for (const key in response) {
          if ((response as any)[key] === 'success') {
            UIDsToRemove.add(key);
          }
        }
      }
      const addedMembers: CometChat.GroupMember[] = [];
      for (let i = 0; i < membersToAddRef.current.length; i++) {
        const curMember = membersToAddRef.current[i];
        if (UIDsToRemove.has(curMember.getUid())) {
          addedMembers.push(curMember);
        }
      }
      const loggedInUser = loggedInUserRef.current;
      if (loggedInUser) {
        const groupClone = CometChatUIKitUtility.clone(group);
        groupClone.setMembersCount(group.getMembersCount() + addedMembers.length);
        CometChatGroupEvents.ccGroupMemberAdded.next({
          messages: addedMembers.map((addedMember) => createActionMessage(addedMember, loggedInUser, groupClone)),
          usersAdded: addedMembers,
          userAddedIn: groupClone,
          userAddedBy: CometChatUIKitUtility.clone(loggedInUser),
        });

        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
        console.log("API_BASE_URL->", API_BASE_URL);
        // 🔹 2. Send your custom notification/email
        await Promise.all(
          addedMembers.map(async (member) => {
            try {
              const response = await axios.get(
                `${COMETCHAT_CONSTANTS.API_URL}/users/${member.getUid()}`,
                {
                  headers: {
                    apikey: COMETCHAT_CONSTANTS.REST_API_KEY,
                  },
                }
              );

              const userData = response.data?.data;

              // const email =
              //   userData?.metadata?.["@private"]?.email || "";

              const privateMeta = userData?.metadata?.["@private"] || {};

              const email = privateMeta?.email;
              const emailEnabled = isEmailEnabled(privateMeta?.email_notifications);

              // ❌ Skip if disabled or no email
              if (!email || !emailEnabled) {
                console.log("⛔ Skipped (added member):", member.getName());
                return;
              }

              const payload = {
                sender_name: loggedInUser.getName(),
                receiver_name: group.getName(),
                receiver_email: email,
                receiver_type: "group",
                type: "text",
                message: `${loggedInUser.getName()} added ${member.getName()}`,
                base_url: `${window.location.origin}/chats/group/${group.getGuid()}`,
                sent_at: Math.floor(Date.now() / 1000), // ✅ correct format
              };

              console.log("Notification Payload ->", payload);

              // 👉 Call your backend notification API here
              try {
                const response = await axios.post(
                  `${API_BASE_URL}/send-chat-email`,
                  payload
                );

                console.log("Email API Success ->", response.data);
              } catch (error: any) {
                console.error("Email API Error ->", error);

                if (error.response) {
                  console.error("Status ->", error.response.status);
                  console.error("Data ->", error.response.data);
                }
              }

            } catch (err) {
              console.error("Error sending notification:", err);
            }
          })
        );
      }
      membersToAddRef.current = [];
      onBackPropRef.current?.();
    } catch (error) {
      setIsLoading(false);
      setIsError(true);
      errorHandler(error);
    }
  }, [errorHandler, createActionMessage, groupPropRef, onAddMembersButtonClickPropRef, onBackPropRef]);

  /**
   * Creates back button view
   */
  function getBackBtnView(): JSX.Element | null {
    if (!showBackButton) {
      return null;
    }
    return (
      <div className="cometchat-add-members__back-button">
        <CometChatButton iconURL={backbutton} onClick={onBack} />
      </div>
    );
  }

  /**
   * Creates add members button view
   */
  function getAddMembersBtnView() {
    return (
      <div
        className={`cometchat-add-members__add-btn-wrapper ${isDisabled ? 'cometchat-add-members__add-btn-wrapper-disabled' : ''}`}
      >
        <CometChatButton isLoading={isLoading} text={buttonText} onClick={onAddBtnClickWrapper} />
      </div>
    );
  }

  const onUsersSelected = (user: CometChat.User) => {
    updateAddMembersList(user);
    if (membersToAddRef.current.length === 0) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  };

  useCometChatAddMembers({
    loggedInUserRef,
    errorHandler,
    selectionMode,
    selectionModeRef,
    membersToAddRef,
  });

  return (
    <div className="cometchat-add-members">
      <CometChatUsers
        hideSearch={hideSearch}
        showSectionHeader={showSectionHeader}
        sectionHeaderKey={sectionHeaderField}
        loadingView={loadingStateView}
        emptyView={emptyStateView}
        errorView={errorStateView}
        hideError={hideError}
        onError={onError}
        options={options}
        selectionMode={selectionMode}
        onSelect={onSelectWrapper}
        usersRequestBuilder={usersRequestBuilder}
        searchRequestBuilder={searchRequestBuilder}
        itemView={listItemView}
        subtitleView={subtitleView}
        onItemClick={onUsersSelected}
        activeUser={undefined}
        hideUserStatus={chatFeatures && !chatFeatures?.coreMessagingExperience?.userAndFriendsPresence}
      />
      {isError ? (
        <div className="cometchat-add-members_error-view">{getLocalizedString('member_error_subtitle')}</div>
      ) : null}
      {getAddMembersBtnView()}
      {getBackBtnView()}
    </div>
  );
}
